// DashboardBuilder.jsx  (src/pages/dashboards/DashboardBuilder.jsx)
//
// Drag-and-drop dashboard builder with multi-screen support.
// Each widget can be assigned to a "screen". Screens cycle automatically
// on the TV display based on dashboard.theme.cycleEnabled / cycleInterval.
//
// All multi-screen state piggybacks on existing JSONB columns — no migrations:
//   • widget.position.screen      (number, default 0)
//   • dashboard.theme.cycleEnabled (bool, default true)
//   • dashboard.theme.cycleInterval (seconds, default 30)
//   • dashboard.theme.screenNames  ({ [screenIndex]: "Custom name" })

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Tv,
  Eye,
  Save,
  X,
  Check,
  Settings,
  Trash2,
  Edit3,
  Plus,
  RefreshCw,
  Monitor,
  GripVertical,
  Rows,
  Columns,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const WIDGET_TYPES = [
  { type: "kpi", label: "KPI / Number", icon: "🔢" },
  { type: "leaderboard", label: "Leaderboard", icon: "🏆" },
  { type: "bar_chart", label: "Bar Chart", icon: "📊" },
  { type: "pie", label: "Pie Chart", icon: "🥧" },
  { type: "donut", label: "Donut Chart", icon: "🍩" },
  { type: "table", label: "Table", icon: "📋" },
  { type: "goal", label: "Goal / Progress", icon: "🎯" },
  { type: "line_chart", label: "Line Chart", icon: "📈" },
];

const DEFAULT_POSITIONS = [
  { x: 0, y: 0, w: 3, h: 2 },
  { x: 3, y: 0, w: 3, h: 2 },
  { x: 6, y: 0, w: 3, h: 2 },
  { x: 9, y: 0, w: 3, h: 2 },
  { x: 0, y: 2, w: 6, h: 3 },
  { x: 6, y: 2, w: 6, h: 3 },
];

const MAX_SCREENS = 10;

// ─── Widget config panel ───────────────────────────────────────────────────

function WidgetConfigPanel({
  widget,
  reports,
  onSave,
  onClose,
  maxScreens,
  isDarkMode,
}) {
  const [form, setForm] = useState({
    title: widget.title || "",
    widgetType: widget.widgetType || "kpi",
    reportId: widget.dataSource?.reportId || "",
    metricField: widget.dataSource?.metricField || "",
    labelField: widget.dataSource?.labelField || "",
    periodOffset: widget.dataSource?.periodOffset || 0,
    refreshInterval: widget.refreshInterval || "",
    goalTarget: widget.config?.goal || "",
    format: widget.config?.format || "number",
    visibleColumns: widget.config?.visibleColumns || [],
    thresholdWarn: widget.config?.threshold?.warn ?? "",
    thresholdGood: widget.config?.threshold?.good ?? "",
    screen: widget.position?.screen ?? 0,
    showBookingBurst: widget.config?.showBookingBurst ?? false,
    showMissedContactBoo: widget.config?.showMissedContactBoo ?? false,
  });

  const selectedReport = reports.find((r) => r.id === form.reportId);
  const columns = selectedReport?.columns || [];

  const handleSave = () => {
    const threshold =
      form.thresholdWarn !== "" && form.thresholdGood !== ""
        ? {
            warn: parseFloat(form.thresholdWarn),
            good: parseFloat(form.thresholdGood),
          }
        : undefined;

    onSave({
      title: form.title,
      widgetType: form.widgetType,
      refreshInterval: form.refreshInterval
        ? parseInt(form.refreshInterval)
        : null,
      position: {
        ...(widget.position || { x: 0, y: 0, w: 3, h: 2 }),
        screen: parseInt(form.screen) || 0,
      },
      dataSource: {
        reportId: form.reportId,
        developerName: selectedReport?.developerName,
        metricField: form.metricField,
        labelField: form.labelField,
        periodOffset: parseInt(form.periodOffset) || 0,
      },
      config: {
        format: form.format,
        goal: form.goalTarget ? parseFloat(form.goalTarget) : undefined,
        label: form.title,
        visibleColumns: form.visibleColumns?.length
          ? form.visibleColumns
          : undefined,
        threshold,
        showBookingBurst: form.showBookingBurst || undefined,
        showMissedContactBoo: form.showMissedContactBoo || undefined,
      },
    });
  };

  const toggleColumn = (field, checked) => {
    setForm((f) => {
      const current = f.visibleColumns?.length
        ? f.visibleColumns
        : columns.map((c) => c.field);
      const next = checked
        ? [...new Set([...current, field])]
        : current.filter((x) => x !== field);
      return { ...f, visibleColumns: next };
    });
  };

  const screenCount = Math.max(maxScreens || 1, form.screen + 1);
  const screenOptions = Array.from({ length: screenCount }, (_, i) => i);

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/40 ${
    isDarkMode
      ? "bg-[#13161B] border-gray-700/50 text-[#F7F7F7] placeholder-[#94979C]"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-xs font-medium mb-1 uppercase tracking-wide ${
    isDarkMode ? "text-[#94979C]" : "text-gray-500"
  }`;

  return (
    <div
      className={`w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh] ${
        isDarkMode
          ? "bg-[#0C0E12] border border-gray-700/50"
          : "bg-white border border-gray-200"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-5 py-4 border-b ${
          isDarkMode ? "border-[#22262F]" : "border-gray-100"
        }`}
      >
        <h3
          className={`font-semibold text-base ${
            isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
          }`}
        >
          Configure Widget
        </h3>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg transition-colors ${
            isDarkMode
              ? "hover:bg-gray-800 text-[#94979C]"
              : "hover:bg-gray-100 text-gray-400"
          }`}
        >
          <X size={16} />
        </button>
      </div>

      {/* Body — scrollable */}
      <div className="overflow-y-auto flex-1 p-5 space-y-4">
        <div>
          <label className={labelClass}>Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Widget title"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Widget Type</label>
          <select
            value={form.widgetType}
            onChange={(e) =>
              setForm((f) => ({ ...f, widgetType: e.target.value }))
            }
            className={inputClass}
          >
            {WIDGET_TYPES.map((wt) => (
              <option key={wt.type} value={wt.type}>
                {wt.icon} {wt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Screen</label>
          <select
            value={form.screen}
            onChange={(e) =>
              setForm((f) => ({ ...f, screen: parseInt(e.target.value) || 0 }))
            }
            className={inputClass}
          >
            {screenOptions.map((i) => (
              <option key={i} value={i}>
                Screen {i + 1}
              </option>
            ))}
          </select>
          <p
            className={`text-xs mt-1 ${
              isDarkMode ? "text-[#94979C]" : "text-gray-400"
            }`}
          >
            Widgets on different screens auto-cycle on the TV display.
          </p>
        </div>

        <div>
          <label className={labelClass}>Report</label>
          <select
            value={form.reportId}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                reportId: e.target.value,
                metricField: "",
                labelField: "",
                visibleColumns: [],
              }))
            }
            className={inputClass}
          >
            <option value="">— Select a report —</option>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        {columns.length > 0 && (
          <>
            <div>
              <label className={labelClass}>Metric Field</label>
              <select
                value={form.metricField}
                onChange={(e) =>
                  setForm((f) => ({ ...f, metricField: e.target.value }))
                }
                className={inputClass}
              >
                <option value="">— Auto-detect —</option>
                {columns
                  .filter((c) =>
                    ["number", "currency", "percentage"].includes(c.type),
                  )
                  .map((c) => (
                    <option key={c.field} value={c.field}>
                      {c.label}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Label Field</label>
              <select
                value={form.labelField}
                onChange={(e) =>
                  setForm((f) => ({ ...f, labelField: e.target.value }))
                }
                className={inputClass}
              >
                <option value="">— Auto-detect —</option>
                {columns
                  .filter((c) => !c.type || c.type === "string")
                  .map((c) => (
                    <option key={c.field} value={c.field}>
                      {c.label}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Visible Columns</label>
              <div
                className={`rounded-lg border p-3 space-y-2 ${
                  isDarkMode
                    ? "border-gray-700/50 bg-[#13161B]"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-400"
                  }`}
                >
                  Leave all unchecked to show every column.
                </p>
                {columns.map((c) => {
                  const vis = form.visibleColumns || [];
                  const checked = vis.length === 0 || vis.includes(c.field);
                  return (
                    <label
                      key={c.field}
                      className={`flex items-center gap-2 text-sm cursor-pointer ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          toggleColumn(c.field, e.target.checked)
                        }
                        className="accent-blue-600"
                      />
                      {c.label || c.field}
                    </label>
                  );
                })}
              </div>
            </div>
          </>
        )}

        <div>
          <label className={labelClass}>Period Offset</label>
          <input
            type="number"
            value={form.periodOffset}
            onChange={(e) =>
              setForm((f) => ({ ...f, periodOffset: e.target.value }))
            }
            placeholder="0 = current, -1 = last…"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Format</label>
          <select
            value={form.format}
            onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
            className={inputClass}
          >
            <option value="number">Number</option>
            <option value="currency">Currency (£)</option>
            <option value="percentage">Percentage (%)</option>
          </select>
        </div>

        {(form.widgetType === "goal" || form.widgetType === "progress") && (
          <div>
            <label className={labelClass}>Goal Target</label>
            <input
              type="number"
              value={form.goalTarget}
              onChange={(e) =>
                setForm((f) => ({ ...f, goalTarget: e.target.value }))
              }
              placeholder="e.g. 120"
              className={inputClass}
            />
          </div>
        )}

        {(form.widgetType === "kpi" || form.widgetType === "number") && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Warn Below</label>
              <input
                type="number"
                value={form.thresholdWarn}
                onChange={(e) =>
                  setForm((f) => ({ ...f, thresholdWarn: e.target.value }))
                }
                placeholder="e.g. 80"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Good Above</label>
              <input
                type="number"
                value={form.thresholdGood}
                onChange={(e) =>
                  setForm((f) => ({ ...f, thresholdGood: e.target.value }))
                }
                placeholder="e.g. 120"
                className={inputClass}
              />
            </div>
          </div>
        )}

        <div>
          <label className={labelClass}>Live Event Alerts</label>
          <label
            className={`flex items-center gap-3 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors ${
              isDarkMode
                ? "border-gray-700/50 bg-[#13161B] text-gray-300 hover:border-gray-600"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300"
            }`}
          >
            <div
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  showBookingBurst: !f.showBookingBurst,
                }))
              }
              style={{
                width: 36,
                height: 20,
                borderRadius: 10,
                background: form.showBookingBurst
                  ? "#818cf8"
                  : isDarkMode
                    ? "#374151"
                    : "#d1d5db",
                position: "relative",
                flexShrink: 0,
                transition: "background 0.2s ease",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: form.showBookingBurst ? 18 : 2,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left 0.2s ease",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            </div>
            <div>
              <div className="text-sm font-medium">
                Show booking &amp; sale bursts
              </div>
              <div
                className={`text-xs mt-0.5 ${isDarkMode ? "text-[#94979C]" : "text-gray-400"}`}
              >
                New appointments and sales will animate on this widget
              </div>
            </div>
          </label>

          <label
            className={`flex items-center gap-3 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors mt-2 ${
              isDarkMode
                ? "border-gray-700/50 bg-[#13161B] text-gray-300 hover:border-gray-600"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300"
            }`}
          >
            <div
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  showMissedContactBoo: !f.showMissedContactBoo,
                }))
              }
              style={{
                width: 36,
                height: 20,
                borderRadius: 10,
                background: form.showMissedContactBoo
                  ? "#f87171"
                  : isDarkMode
                    ? "#374151"
                    : "#d1d5db",
                position: "relative",
                flexShrink: 0,
                transition: "background 0.2s ease",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: form.showMissedContactBoo ? 18 : 2,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left 0.2s ease",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            </div>
            <div>
              <div className="text-sm font-medium">Show missed-contact boo</div>
              <div
                className={`text-xs mt-0.5 ${isDarkMode ? "text-[#94979C]" : "text-gray-400"}`}
              >
                Overdue 4-hour contact timers will trigger a boo animation on
                this widget
              </div>
            </div>
          </label>
        </div>

        <div>
          <label className={labelClass}>Refresh Interval (seconds)</label>
          <input
            type="number"
            value={form.refreshInterval}
            onChange={(e) =>
              setForm((f) => ({ ...f, refreshInterval: e.target.value }))
            }
            placeholder="Blank = inherit dashboard"
            className={inputClass}
          />
        </div>
      </div>

      {/* Footer */}
      <div
        className={`flex items-center justify-end gap-3 px-5 py-4 border-t ${
          isDarkMode ? "border-[#22262F]" : "border-gray-100"
        }`}
      >
        <button
          onClick={onClose}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isDarkMode
              ? "hover:bg-gray-800 text-gray-300"
              : "hover:bg-gray-100 text-gray-600"
          }`}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!form.reportId}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
        >
          Save Widget
        </button>
      </div>
    </div>
  );
}
function LogoUploader({ dashboard, dashboardId, onSave, isDarkMode }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const currentLogo = dashboard?.theme?.logoUrl || null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const result = await apiService.uploadDashboardLogo(dashboardId, file);
      if (result.success) {
        await onSave({ logoUrl: result.data.logoUrl });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = async () => {
    await onSave({ logoUrl: null });
  };

  return (
    <div
      className={`mx-4 mb-4 border-t pt-4 ${
        isDarkMode ? "border-[#22262F]" : "border-gray-100"
      }`}
    >
      <p
        className={`text-xs font-bold uppercase tracking-widest mb-3 ${
          isDarkMode ? "text-[#94979C]" : "text-gray-400"
        }`}
      >
        Logo
      </p>

      {currentLogo ? (
        <div className="space-y-2">
          <div
            className={`rounded-lg p-2 flex items-center justify-center border ${
              isDarkMode
                ? "border-gray-700/50 bg-[#13161B]"
                : "border-gray-200 bg-gray-50"
            }`}
            style={{ minHeight: 48 }}
          >
            <img
              src={currentLogo}
              alt="Dashboard logo"
              className="max-h-10 max-w-full object-contain"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                isDarkMode
                  ? "border-gray-700/50 text-gray-300 hover:bg-gray-800"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              Replace
            </button>
            <button
              onClick={handleRemove}
              disabled={uploading}
              className="flex-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={`w-full flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg border-2 border-dashed text-xs font-medium transition-colors ${
            isDarkMode
              ? "border-gray-700/50 text-[#94979C] hover:border-gray-600 hover:text-gray-300"
              : "border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600"
          }`}
        >
          {uploading ? (
            <>
              <RefreshCw size={14} className="animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <span className="text-base">🖼</span>
              Upload Logo
            </>
          )}
        </button>
      )}

      {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}

// ─── TV Panel ──────────────────────────────────────────────────────────────

function TVPanel({ dashboard, onClose, isDarkMode }) {
  const [tvs, setTVs] = useState([]);
  const [pairingCode, setPairingCode] = useState(null);
  const [tvName, setTVName] = useState("Sales Floor TV");
  const [loadingTVs, setLoadingTVs] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const tvUrl = `${window.location.origin}/tv/${dashboard.shareToken}`;

  useEffect(() => {
    apiService
      .getTVDisplays()
      .then((d) => setTVs(d.data || []))
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoadingTVs(false));
  }, []);

  const generateCode = async () => {
    setGenerating(true);
    setError(null);
    try {
      const d = await apiService.createTVPairingCode(dashboard.id, tvName);
      setPairingCode(d.data.pairingCode);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(tvUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pairedTVs = tvs.filter((tv) => tv.dashboardId === dashboard.id);

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-blue-500/40 ${
    isDarkMode
      ? "bg-[#13161B] border-gray-700/50 text-[#F7F7F7] placeholder-[#94979C]"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const sectionLabel = `block text-xs font-medium mb-2 uppercase tracking-wide ${
    isDarkMode ? "text-[#94979C]" : "text-gray-500"
  }`;

  const statusColors = {
    online: "bg-green-500",
    offline: "bg-gray-500",
    idle: "bg-yellow-500",
  };

  return (
    <div
      className={`w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh] ${
        isDarkMode
          ? "bg-[#0C0E12] border border-gray-700/50"
          : "bg-white border border-gray-200"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-5 py-4 border-b ${
          isDarkMode ? "border-[#22262F]" : "border-gray-100"
        }`}
      >
        <div className="flex items-center gap-2">
          <Tv size={18} className="text-blue-500" />
          <h3
            className={`font-semibold text-base ${
              isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
            }`}
          >
            Send to TV
          </h3>
        </div>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-lg transition-colors ${
            isDarkMode
              ? "hover:bg-gray-800 text-[#94979C]"
              : "hover:bg-gray-100 text-gray-400"
          }`}
        >
          <X size={16} />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 p-5 space-y-5">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* TV URL */}
        <div>
          <label className={sectionLabel}>Public TV URL</label>
          <div className="flex gap-2">
            <input readOnly value={tvUrl} className={`${inputClass} flex-1`} />
            <button
              onClick={copyUrl}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                copied
                  ? "bg-green-500/10 border-green-500/20 text-green-500"
                  : isDarkMode
                    ? "border-gray-700/50 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {copied ? <Check size={14} /> : null}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p
            className={`text-xs mt-2 ${
              isDarkMode ? "text-[#94979C]" : "text-gray-400"
            }`}
          >
            Load in Chrome kiosk mode, Fire Stick Silk browser, or any TV
            browser. No login required.
          </p>
        </div>

        <div
          className={`border-t ${
            isDarkMode ? "border-[#22262F]" : "border-gray-100"
          }`}
        />

        {/* Pair new TV */}
        <div>
          <label className={sectionLabel}>Pair a New TV Screen</label>
          <input
            value={tvName}
            onChange={(e) => setTVName(e.target.value)}
            placeholder="TV name (e.g. Sales Floor)"
            className={`${inputClass} mb-3`}
          />
          <button
            onClick={generateCode}
            disabled={generating}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {generating ? "Generating…" : "Generate Pairing Code"}
          </button>
          {pairingCode && (
            <div
              className={`mt-4 rounded-xl border p-4 text-center ${
                isDarkMode
                  ? "border-blue-500/30 bg-blue-500/5"
                  : "border-blue-200 bg-blue-50"
              }`}
            >
              <div
                className={`text-4xl font-bold tracking-[0.4em] mb-2 ${
                  isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
                }`}
              >
                {pairingCode}
              </div>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-[#94979C]" : "text-gray-500"
                }`}
              >
                Enter this code on the TV to pair it. Expires in 10 minutes.
              </p>
            </div>
          )}
        </div>

        {/* Paired TVs */}
        {!loadingTVs && pairedTVs.length > 0 && (
          <>
            <div
              className={`border-t ${
                isDarkMode ? "border-[#22262F]" : "border-gray-100"
              }`}
            />
            <div>
              <label className={sectionLabel}>Paired TVs</label>
              <div className="space-y-2">
                {pairedTVs.map((tv) => (
                  <div
                    key={tv.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                      isDarkMode
                        ? "border-gray-700/50 bg-[#13161B]"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        statusColors[tv.status] || "bg-gray-500"
                      }`}
                    />
                    <span
                      className={`flex-1 text-sm ${
                        isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
                      }`}
                    >
                      {tv.name}
                    </span>
                    <span
                      className={`text-xs capitalize ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-400"
                      }`}
                    >
                      {tv.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── DragGrid ──────────────────────────────────────────────────────────────
// Renders the 12-column grid with HTML5 drag-to-reposition and
// inline row/col span controls. Calls onUpdatePosition with position patches
// so the parent can persist them immediately via the API.

const GRID_COLS = 12;
const ROW_PX = 80;
const GAP_PX = 16;

function DragGrid({
  widgets,
  reports,
  isDarkMode,
  onUpdatePosition,
  onEdit,
  onRemove,
}) {
  const gridRef = useRef(null);
  const dragState = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const pxToCell = useCallback((clientX, clientY) => {
    const grid = gridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const colW = (rect.width + GAP_PX) / GRID_COLS;
    const rowH = ROW_PX + GAP_PX;
    return {
      col: Math.max(0, Math.floor((clientX - rect.left) / colW)),
      row: Math.max(0, Math.floor((clientY - rect.top) / rowH)),
    };
  }, []);

  const handleDragStart = useCallback(
    (e, widget) => {
      const pos = widget.position || { x: 0, y: 0, w: 3, h: 2 };
      const cell = pxToCell(e.clientX, e.clientY);
      dragState.current = {
        widgetId: widget.id,
        offsetCol: cell ? cell.col - pos.x : 0,
        offsetRow: cell ? cell.row - pos.y : 0,
        w: pos.w,
        h: pos.h,
      };
      setDraggingId(widget.id);
      const blank = document.createElement("div");
      blank.style.cssText =
        "width:1px;height:1px;opacity:0;position:fixed;top:0;left:0";
      document.body.appendChild(blank);
      e.dataTransfer.setDragImage(blank, 0, 0);
      setTimeout(() => document.body.removeChild(blank), 0);
    },
    [pxToCell],
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      if (!dragState.current) return;
      const { offsetCol, offsetRow, w } = dragState.current;
      const cell = pxToCell(e.clientX, e.clientY);
      if (!cell) return;
      setDropTarget({
        x: Math.max(0, Math.min(GRID_COLS - w, cell.col - offsetCol)),
        y: Math.max(0, cell.row - offsetRow),
      });
    },
    [pxToCell],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      if (!dragState.current || !dropTarget) return;
      onUpdatePosition(dragState.current.widgetId, {
        x: dropTarget.x,
        y: dropTarget.y,
      });
      setDropTarget(null);
      setDraggingId(null);
      dragState.current = null;
    },
    [dropTarget, onUpdatePosition],
  );

  const handleDragEnd = useCallback(() => {
    setDropTarget(null);
    setDraggingId(null);
    dragState.current = null;
  }, []);

  const ROW_OPTIONS = [1, 2, 3, 4];
  const COL_OPTIONS = [
    { w: 3, label: "¼" },
    { w: 4, label: "⅓" },
    { w: 6, label: "½" },
    { w: 8, label: "⅔" },
    { w: 12, label: "Full" },
  ];

  return (
    <div
      ref={gridRef}
      className="grid gap-4"
      style={{
        gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
        gridAutoRows: `${ROW_PX}px`,
        minHeight: "320px",
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {widgets.map((widget) => {
        const pos = widget.position || { x: 0, y: 0, w: 3, h: 2 };
        const isDragging = draggingId === widget.id;
        const displayPos =
          isDragging && dropTarget
            ? { ...pos, x: dropTarget.x, y: dropTarget.y }
            : pos;

        return (
          <div
            key={widget.id}
            draggable
            onDragStart={(e) => handleDragStart(e, widget)}
            onDragEnd={handleDragEnd}
            className={`rounded-xl border transition-all select-none ${
              isDragging
                ? isDarkMode
                  ? "opacity-40 border-blue-500/60 bg-[#0C0E12]"
                  : "opacity-40 border-blue-400 bg-white"
                : isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50"
                  : "bg-white border-gray-200 hover:border-gray-300"
            }`}
            style={{
              gridColumn: `${displayPos.x + 1} / span ${displayPos.w}`,
              gridRow: `${displayPos.y + 1} / span ${displayPos.h}`,
              cursor: "grab",
            }}
          >
            <div className="h-full flex flex-col p-3 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between mb-2 shrink-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className={`shrink-0 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
                  >
                    <GripVertical size={14} />
                  </span>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full truncate ${
                      isDarkMode
                        ? "bg-[#13161B] text-[#94979C]"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {
                      WIDGET_TYPES.find((wt) => wt.type === widget.widgetType)
                        ?.icon
                    }{" "}
                    {widget.widgetType}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(widget);
                    }}
                    className={`p-1 rounded transition-colors ${
                      isDarkMode
                        ? "hover:bg-gray-800 text-[#94979C] hover:text-gray-300"
                        : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <Edit3 size={12} />
                  </button>
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(widget.id);
                    }}
                    className="p-1 rounded transition-colors hover:bg-red-500/10 text-red-400"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col justify-center min-h-0 overflow-hidden">
                <p
                  className={`text-sm font-semibold leading-snug truncate ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
                >
                  {widget.title || "Untitled"}
                </p>
                <p
                  className={`text-xs mt-0.5 truncate ${isDarkMode ? "text-[#94979C]" : "text-gray-400"}`}
                >
                  {reports.find((r) => r.id === widget.dataSource?.reportId)
                    ?.name || "No report selected"}
                </p>
              </div>

              {/* Size controls */}
              <div
                className="shrink-0 mt-2 pt-2 border-t flex items-center gap-2"
                style={{
                  borderColor: isDarkMode
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.07)",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-1">
                  <Rows
                    size={11}
                    className={isDarkMode ? "text-gray-600" : "text-gray-300"}
                  />
                  {ROW_OPTIONS.map((h) => (
                    <button
                      key={h}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdatePosition(widget.id, { h });
                      }}
                      title={`${h} row${h > 1 ? "s" : ""} tall`}
                      className={`w-5 h-5 rounded text-[10px] font-semibold transition-colors ${
                        pos.h === h
                          ? "bg-blue-600 text-white"
                          : isDarkMode
                            ? "bg-[#13161B] text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                            : "bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                      }`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 ml-auto">
                  <Columns
                    size={11}
                    className={isDarkMode ? "text-gray-600" : "text-gray-300"}
                  />
                  {COL_OPTIONS.map(({ w, label }) => (
                    <button
                      key={w}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdatePosition(widget.id, {
                          w,
                          x: Math.min(pos.x, GRID_COLS - w),
                        });
                      }}
                      title={`${w} columns wide`}
                      className={`px-1.5 h-5 rounded text-[10px] font-semibold transition-colors ${
                        pos.w === w
                          ? "bg-blue-600 text-white"
                          : isDarkMode
                            ? "bg-[#13161B] text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                            : "bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Drop ghost */}
      {draggingId &&
        dropTarget &&
        (() => {
          const dw = dragState.current?.w ?? 3;
          const dh = dragState.current?.h ?? 2;
          return (
            <div
              className="rounded-xl border-2 border-dashed border-blue-500/50 bg-blue-500/5 pointer-events-none"
              style={{
                gridColumn: `${dropTarget.x + 1} / span ${dw}`,
                gridRow: `${dropTarget.y + 1} / span ${dh}`,
              }}
            />
          );
        })()}
    </div>
  );
}

// ─── Main Builder ──────────────────────────────────────────────────────────

export default function DashboardBuilder({ dashboardId: dashboardIdProp }) {
  const { id } = useParams();
  const dashboardId = dashboardIdProp || id;
  const { isDarkMode } = useTheme();

  const [dashboard, setDashboard] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [reports, setReports] = useState([]);
  const [editingWidget, setEditingWidget] = useState(null);
  const [showTVPanel, setShowTVPanel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Multi-screen state
  const [activeScreen, setActiveScreen] = useState(0);
  const [cycleEnabled, setCycleEnabled] = useState(true);
  const [cycleInterval, setCycleInterval] = useState(30);
  const [screenNames, setScreenNames] = useState({});
  const [renamingScreen, setRenamingScreen] = useState(null);

  useEffect(() => {
    Promise.all([
      apiService.getDashboard(dashboardId),
      apiService.getReports({ limit: 200 }),
    ])
      .then(([dash, reps]) => {
        if (dash.success) {
          setDashboard(dash.data);
          setWidgets(dash.data.widgets || []);
          const theme = dash.data.theme || {};
          setCycleEnabled(theme.cycleEnabled !== false);
          setCycleInterval(theme.cycleInterval ?? 30);
          setScreenNames(theme.screenNames || {});
        }
        if (reps.success) setReports(reps.data || []);
      })
      .catch((err) => setLoadError(err.response?.data?.message || err.message));
  }, [dashboardId]);

  // Derived screen metadata
  const screensInUse = [
    ...new Set(widgets.map((w) => w.position?.screen ?? 0)),
  ].sort((a, b) => a - b);

  const highestScreen = Math.max(0, ...screensInUse, activeScreen);
  const screenOptions = Array.from({ length: highestScreen + 1 }, (_, i) => i);
  const maxScreensForConfig = Math.min(MAX_SCREENS, highestScreen + 2);

  const visibleWidgets = widgets.filter(
    (w) => (w.position?.screen ?? 0) === activeScreen,
  );

  // Persists every widget's current position to the DB.
  // Called by the Save Layout button as a bulk flush.
  const saveLayout = useCallback(async () => {
    setSaving(true);
    try {
      // Save each widget's position individually — this is what actually
      // persists x/y/w/h on the DashboardWidget rows in the DB.
      await Promise.all(
        widgets.map((w) =>
          apiService.updateDashboardWidget(dashboardId, w.id, {
            position: w.position,
          }),
        ),
      );
      // Also update the Dashboard.layout array for any consumers that read it.
      await apiService.saveDashboardLayout(
        dashboardId,
        widgets.map((w) => ({ i: w.id, ...w.position })),
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save layout error:", err);
    } finally {
      setSaving(false);
    }
  }, [dashboardId, widgets]);

  // Called immediately on drag-drop or span change — patches local state
  // AND persists the position change to the DB right away so it's never lost.
  const updateWidgetPosition = useCallback(
    async (widgetId, positionPatch) => {
      // Optimistic local update first
      setWidgets((prev) =>
        prev.map((w) =>
          w.id === widgetId
            ? { ...w, position: { ...w.position, ...positionPatch } }
            : w,
        ),
      );
      // Persist to DB
      try {
        const widget = widgets.find((w) => w.id === widgetId);
        if (!widget) return;
        await apiService.updateDashboardWidget(dashboardId, widgetId, {
          position: { ...widget.position, ...positionPatch },
        });
      } catch (err) {
        console.error("Position save error:", err);
      }
    },
    [dashboardId, widgets],
  );

  const saveThemePatch = useCallback(
    async (patch) => {
      try {
        const newTheme = { ...(dashboard?.theme || {}), ...patch };
        await apiService.updateDashboard(dashboardId, { theme: newTheme });
        setDashboard((d) => (d ? { ...d, theme: newTheme } : d));
      } catch (err) {
        console.error("Save theme error:", err);
      }
    },
    [dashboardId, dashboard],
  );

  const addWidget = (type) => {
    const widgetsOnThisScreen = widgets.filter(
      (w) => (w.position?.screen ?? 0) === activeScreen,
    );
    const position = {
      ...DEFAULT_POSITIONS[
        widgetsOnThisScreen.length % DEFAULT_POSITIONS.length
      ],
      screen: activeScreen,
    };
    const newWidget = {
      widgetType: type,
      title: WIDGET_TYPES.find((wt) => wt.type === type)?.label,
      position,
      dataSource: {},
      config: {},
    };
    setEditingWidget({ widget: newWidget, isNew: true });
  };

  const saveWidget = async (configData) => {
    const { widget, isNew } = editingWidget;
    const payload = { ...widget, ...configData };

    try {
      if (isNew) {
        const d = await apiService.addDashboardWidget(dashboardId, payload);
        if (d.success) setWidgets((prev) => [...prev, d.data]);
      } else {
        const d = await apiService.updateDashboardWidget(
          dashboardId,
          widget.id,
          payload,
        );
        if (d.success)
          setWidgets((prev) =>
            prev.map((w) => (w.id === widget.id ? d.data : w)),
          );
      }
    } catch (err) {
      console.error("Save widget error:", err);
    }

    setEditingWidget(null);
  };

  const removeWidget = async (widgetId) => {
    if (!window.confirm("Remove this widget?")) return;
    try {
      await apiService.removeDashboardWidget(dashboardId, widgetId);
      setWidgets((prev) => prev.filter((w) => w.id !== widgetId));
    } catch (err) {
      console.error("Remove widget error:", err);
    }
  };

  const handleCycleEnabledChange = (val) => {
    setCycleEnabled(val);
    saveThemePatch({ cycleEnabled: val });
  };

  const handleCycleIntervalBlur = () => {
    const parsed = parseInt(cycleInterval, 10);
    const safe = isNaN(parsed) || parsed < 5 ? 30 : parsed;
    setCycleInterval(safe);
    saveThemePatch({ cycleInterval: safe });
  };

  const handleAddScreen = () => {
    if (highestScreen + 1 >= MAX_SCREENS) return;
    setActiveScreen(highestScreen + 1);
  };

  const handleRenameScreen = (screenIdx, newName) => {
    const trimmed = (newName || "").trim();
    const next = { ...screenNames };
    if (trimmed) {
      next[screenIdx] = trimmed;
    } else {
      delete next[screenIdx];
    }
    setScreenNames(next);
    saveThemePatch({ screenNames: next });
    setRenamingScreen(null);
  };

  const getScreenLabel = (s) => screenNames[s] || `Screen ${s + 1}`;

  if (loadError) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${
          isDarkMode ? "bg-[#0A0B0F] text-red-400" : "bg-[#F4F5F7] text-red-600"
        }`}
      >
        <div className="text-center">
          <p className="text-sm font-medium">Error loading dashboard</p>
          <p className="text-xs mt-1 opacity-70">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${
          isDarkMode
            ? "bg-[#0A0B0F] text-[#94979C]"
            : "bg-[#F4F5F7] text-gray-400"
        }`}
      >
        <div className="flex items-center gap-3">
          <RefreshCw size={18} className="animate-spin" />
          <span className="text-sm">Loading dashboard…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-screen overflow-hidden transition-colors duration-300 ${
        isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"
      }`}
    >
      {/* ── Toolbar ── */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
          isDarkMode
            ? "bg-[#0C0E12] border-[#22262F]"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="flex items-center gap-3">
          <Monitor
            size={18}
            className={isDarkMode ? "text-[#94979C]" : "text-gray-400"}
          />
          <span
            className={`font-semibold text-base ${
              isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"
            }`}
          >
            {dashboard.name}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(`/tv/${dashboard.shareToken}`, "_blank")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              isDarkMode
                ? "border-gray-700/50 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Eye size={14} />
            Preview TV
          </button>
          <button
            onClick={() => setShowTVPanel(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              isDarkMode
                ? "border-gray-700/50 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Tv size={14} />
            Send to TV
          </button>
          <button
            onClick={saveLayout}
            disabled={saving}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              saved
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            }`}
          >
            {saved ? (
              <>
                <Check size={14} />
                Saved
              </>
            ) : (
              <>
                <Save size={14} />
                {saving ? "Saving…" : "Save Layout"}
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Widget Palette (left sidebar) ── */}
        <div
          className={`w-56 shrink-0 border-r flex flex-col overflow-y-auto ${
            isDarkMode
              ? "bg-[#0C0E12] border-[#22262F]"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="p-4">
            <p
              className={`text-xs font-bold uppercase tracking-widest mb-3 ${
                isDarkMode ? "text-[#94979C]" : "text-gray-400"
              }`}
            >
              Add Widget
            </p>
            <div className="space-y-1">
              {WIDGET_TYPES.map((wt) => (
                <button
                  key={wt.type}
                  onClick={() => addWidget(wt.type)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${
                    isDarkMode
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span className="text-base leading-none">{wt.icon}</span>
                  {wt.label}
                </button>
              ))}
            </div>
          </div>
          {/* ── Logo upload ── */}
          <LogoUploader
            dashboard={dashboard}
            dashboardId={dashboardId}
            onSave={saveThemePatch}
            isDarkMode={isDarkMode}
          />
          {/* Screen cycling config */}
          <div
            className={`mx-4 mb-4 mt-auto border-t pt-4 ${
              isDarkMode ? "border-[#22262F]" : "border-gray-100"
            }`}
          >
            <p
              className={`text-xs font-bold uppercase tracking-widest mb-3 ${
                isDarkMode ? "text-[#94979C]" : "text-gray-400"
              }`}
            >
              Screen Cycling
            </p>

            <label
              className={`flex items-center justify-between text-sm cursor-pointer mb-3 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Auto-cycle
              <button
                onClick={() => handleCycleEnabledChange(!cycleEnabled)}
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  cycleEnabled
                    ? "bg-blue-600"
                    : isDarkMode
                      ? "bg-gray-700"
                      : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    cycleEnabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </label>

            {cycleEnabled && (
              <div>
                <label
                  className={`block text-xs mb-1 ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-400"
                  }`}
                >
                  Interval (seconds)
                </label>
                <input
                  type="number"
                  min="5"
                  value={cycleInterval}
                  onChange={(e) => setCycleInterval(e.target.value)}
                  onBlur={handleCycleIntervalBlur}
                  className={`w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/40 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700/50 text-[#F7F7F7]"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            )}

            <p
              className={`text-xs mt-2 leading-snug ${
                isDarkMode ? "text-[#94979C]" : "text-gray-400"
              }`}
            >
              Widgets on different screens rotate on the TV automatically.
            </p>
          </div>
        </div>

        {/* ── Canvas ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Screen tabs bar */}
          <div
            className={`flex items-center gap-2 px-4 py-2.5 border-b shrink-0 flex-wrap ${
              isDarkMode
                ? "bg-[#13161B] border-[#22262F]"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <span
              className={`text-xs font-bold uppercase tracking-widest mr-1 ${
                isDarkMode ? "text-[#94979C]" : "text-gray-400"
              }`}
            >
              Screens
            </span>

            {screenOptions.map((s) => {
              const count = widgets.filter(
                (w) => (w.position?.screen ?? 0) === s,
              ).length;
              const active = activeScreen === s;
              const isRenaming = renamingScreen === s;

              if (isRenaming) {
                return (
                  <input
                    key={s}
                    autoFocus
                    defaultValue={screenNames[s] || ""}
                    placeholder={`Screen ${s + 1}`}
                    onBlur={(e) => handleRenameScreen(s, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleRenameScreen(s, e.target.value);
                      if (e.key === "Escape") setRenamingScreen(null);
                    }}
                    className={`px-3 py-1 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500/40 min-w-[100px] ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-blue-500 text-[#F7F7F7]"
                        : "bg-white border-blue-400 text-gray-900"
                    }`}
                  />
                );
              }

              return (
                <button
                  key={s}
                  onClick={() => setActiveScreen(s)}
                  onDoubleClick={() => setRenamingScreen(s)}
                  title="Double-click to rename"
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium border transition-colors ${
                    active
                      ? "bg-blue-600 border-blue-600 text-white"
                      : isDarkMode
                        ? "border-gray-700/50 text-gray-300 hover:bg-gray-800"
                        : "border-gray-300 text-gray-600 hover:bg-gray-100 bg-white"
                  }`}
                >
                  {getScreenLabel(s)}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      active
                        ? "bg-white/20 text-white"
                        : isDarkMode
                          ? "bg-[#22262F] text-[#94979C]"
                          : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}

            {highestScreen + 1 < MAX_SCREENS && (
              <button
                onClick={handleAddScreen}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm border border-dashed transition-colors ${
                  isDarkMode
                    ? "border-gray-700/50 text-[#94979C] hover:border-gray-600 hover:text-gray-300"
                    : "border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600"
                }`}
              >
                <Plus size={13} />
                Add Screen
              </button>
            )}

            {/* Cycle status */}
            <div className="ml-auto flex items-center gap-1.5">
              {cycleEnabled && screensInUse.length > 1 ? (
                <span
                  className={`text-xs flex items-center gap-1 ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-400"
                  }`}
                >
                  <RefreshCw size={11} className="animate-spin-slow" />
                  Every {cycleInterval}s
                </span>
              ) : screensInUse.length > 1 ? (
                <span
                  className={`text-xs ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-400"
                  }`}
                >
                  Cycling paused
                </span>
              ) : null}
            </div>
          </div>

          {/* Canvas grid */}
          <div
            className={`flex-1 overflow-auto p-4 ${
              isDarkMode ? "bg-[#0A0B0F]" : "bg-[#F4F5F7]"
            }`}
          >
            {visibleWidgets.length === 0 ? (
              <div
                className={`h-full min-h-64 rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-center p-8 ${
                  isDarkMode ? "border-gray-700/50" : "border-gray-300"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                    isDarkMode ? "bg-[#13161B]" : "bg-gray-100"
                  }`}
                >
                  <span className="text-2xl">📊</span>
                </div>
                <p
                  className={`text-sm font-medium mb-1 ${
                    isDarkMode ? "text-[#F7F7F7]" : "text-gray-700"
                  }`}
                >
                  {getScreenLabel(activeScreen)} is empty
                </p>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-[#94979C]" : "text-gray-400"
                  }`}
                >
                  Add widgets from the palette — they'll be placed on this
                  screen.
                </p>
              </div>
            ) : (
              <DragGrid
                widgets={visibleWidgets}
                reports={reports}
                isDarkMode={isDarkMode}
                onUpdatePosition={updateWidgetPosition}
                onEdit={(widget) => setEditingWidget({ widget, isNew: false })}
                onRemove={removeWidget}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Widget config modal ── */}
      {editingWidget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setEditingWidget(null)}
        >
          <WidgetConfigPanel
            widget={editingWidget.widget}
            reports={reports}
            onSave={saveWidget}
            onClose={() => setEditingWidget(null)}
            maxScreens={maxScreensForConfig}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {/* ── TV panel modal ── */}
      {showTVPanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowTVPanel(false)}
        >
          <TVPanel
            dashboard={dashboard}
            onClose={() => setShowTVPanel(false)}
            isDarkMode={isDarkMode}
          />
        </div>
      )}
    </div>
  );
}
